import React, { createContext, useCallback, useContext, useState } from 'react';
import { getTestDataMode, setTestDataMode } from '../services/rosterFixtures';

/**
 * TestDataBannerContext - holds the always-present global "test data" banner
 * state (rendered above the navbar by GlobalTestDataBanner). Its main job is the
 * season-phase `mode` (pre-draft / mid-season / after-season) that drives which
 * sample data the app shows. Pages may still publish a contextual `note` via
 * showBanner({ note }) / hideBanner().
 */
const TestDataBannerContext = createContext(null);

export function TestDataBannerProvider({ children }) {
  const [content, setContent] = useState(null); // { note } | null
  const [mode, setModeState] = useState(() => getTestDataMode());

  const showBanner = useCallback((next) => setContent(next), []);
  const hideBanner = useCallback(() => setContent(null), []);
  const setMode = useCallback((m) => {
    setTestDataMode(m); // persist + clear cached fixture rosters so they rebuild
    setModeState(m);
  }, []);

  return (
    <TestDataBannerContext.Provider value={{ content, showBanner, hideBanner, mode, setMode }}>
      {children}
    </TestDataBannerContext.Provider>
  );
}

export function useTestDataBanner() {
  const ctx = useContext(TestDataBannerContext);
  if (!ctx) {
    return { content: null, showBanner: () => {}, hideBanner: () => {}, mode: 'mid-season', setMode: () => {} };
  }
  return ctx;
}

export default TestDataBannerContext;
